import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import Mark from 'mark.js';
import style from './ocrPreviewModal.module.css';
import mainStyles from '../../pages/main.module.css';
import Button from '../Button/button';
import PdfOCRPreview from '../PdfPreview/PdfOcrPreview';
import CopyButton from '../CopyButton/CopyButton';
import { OcrDocument } from '../../services/http/documents.api';
import Spinner from '../Spinner';
import PdfPreviewSideSlide from '../PdfPreview/PdfPreviewSideSlide';
import BaseText from '../BaseText/BaseText';
import { useMessageModal } from '../../hooks/modal.hook';
import { ERROR_MESSAGES } from '../../utils/messages';

interface OcrDetailResult {
  ocrText: string;
}

interface OcrPage {
  pageNo: number;
  ocrDetailResultList: OcrDetailResult[];
}

type HighlightMap = { pageNo: number }[];

export interface PdfPreviewProps {
  open: boolean;
  onClose: () => void;
  pdfFile: File | null;
  ocrDetailData?: OcrDocument;
  searchKeyword?: string;
}

export function OcrPreviewModal({
  open = false,
  onClose,
  pdfFile,
  ocrDetailData,
  searchKeyword,
}: PdfPreviewProps) {
  // hooks
  const openMessageModal = useMessageModal();

  // refs
  const ocrContainerRef = useRef<HTMLDivElement>(null);
  const markInstanceRef = useRef<Mark | null>(null);

  // states
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOCRData, setSelectedOCRData] = React.useState<OcrPage | null>(null);
  const [copiedText, setCopiedText] = useState('');
  const [currentHighlightIndex, setCurrentHighlightIndex] = useState(0);
  const [highlightMap, setHighlightMap] = useState<HighlightMap>([]);
  const [copiedItemId, setCopiedItemId] = useState<string>('');
  const [previousPageLastLineText, setPreviousPageLastLineText] = useState<string | null>(null);
  const [nextPageFirstLineText, setNextPageFirstLineText] = useState<string | null>(null);
  const [groupedMarks, setGroupedMarks] = useState<HTMLElement[][]>([]);

  // constants
  const totalMatches = highlightMap.length;
  const isSearching = !!searchKeyword;
  const highlightWords = ocrDetailData?.ocrHighlightWordList ?? [];

  // methods
  const handleCloseModal = useCallback(() => {
    setCurrentPage(1);
    setCurrentHighlightIndex(0);
    setHighlightMap([]);
    onClose();
  }, [onClose]);

  const handleNextHighlight = useCallback(() => {
    if (totalMatches === 0) return;

    const nextIndex = (currentHighlightIndex + 1) % totalMatches;
    const targetPage = highlightMap[nextIndex].pageNo;

    setCurrentHighlightIndex(nextIndex);
    if (targetPage !== currentPage) {
      setCurrentPage(targetPage);
    }
  }, [currentHighlightIndex, totalMatches, highlightMap, currentPage]);

  const handlePreviousHighlight = useCallback(() => {
    if (totalMatches === 0) return;

    const prevIndex = (currentHighlightIndex - 1 + totalMatches) % totalMatches;
    const targetPage = highlightMap[prevIndex].pageNo;

    setCurrentHighlightIndex(prevIndex);
    if (targetPage !== currentPage) {
      setCurrentPage(targetPage);
    }
  }, [currentHighlightIndex, totalMatches, highlightMap, currentPage]);

  const buildGlobalTempContainer = (ocrResultList: OcrPage[]): HTMLDivElement => {
    const tempContainer = document.createElement('div');
    tempContainer.style.display = 'none';

    let globalLineId = 0;

    tempContainer.innerHTML = ocrResultList
      .map((page) => page.ocrDetailResultList
        .map((item) => {
          // eslint-disable-next-line no-plusplus
          globalLineId++;
          return `<div data-page="${page.pageNo}" data-line-id="${globalLineId}">${item.ocrText}</div>`;
        })
        .join(''))
      .join('');

    document.body.appendChild(tempContainer);
    return tempContainer;
  };

  const isLastWordInLine = (node: Node): boolean => {
    let currentNode = node.nextSibling;
    while (currentNode) {
      const isEmptyTextNode = currentNode.nodeType === Node.TEXT_NODE && currentNode.textContent?.trim() === '';
      if (!isEmptyTextNode) {
        return false;
      }
      currentNode = currentNode.nextSibling;
    }
    return true;
  };

  const isFirstWordInLine = (node: Node): boolean => {
    let currentNode = node.previousSibling;
    while (currentNode) {
      const isEmptyTextNode = currentNode.nodeType === Node.TEXT_NODE && currentNode.textContent?.trim() === '';
      if (!isEmptyTextNode) {
        return false;
      }
      currentNode = currentNode.previousSibling;
    }
    return true;
  };

  const shouldBreakMarkGrouping = (currentMark: Element, nextMark: Element): boolean => {
    const currentLineElement = currentMark.closest('[data-line-id]');
    const nextLineElement = nextMark.closest('[data-line-id]');
    const currentPageElement = currentMark.closest('[data-page]');
    const nextPageElement = nextMark.closest('[data-page]');

    const isOnSameLine = currentLineElement?.getAttribute('data-line-id') === nextLineElement?.getAttribute('data-line-id');
    if (isOnSameLine) {
      return true;
    }

    const isAtLineBoundaries = isLastWordInLine(currentMark) && isFirstWordInLine(nextMark);
    if (!isAtLineBoundaries) {
      return true;
    }

    const isOnSamePage = currentPageElement?.getAttribute('data-page') === nextPageElement?.getAttribute('data-page');
    if (isOnSamePage) {
      const currentLineId = Number(currentLineElement?.getAttribute('data-line-id'));
      const nextLineId = Number(nextLineElement?.getAttribute('data-line-id'));
      const isConsecutiveLines = nextLineId === currentLineId + 1;

      if (!isConsecutiveLines) {
        return true;
      }
    }

    return false;
  };

  const groupMarkElementsByHighlight = (
    markElements: NodeListOf<Element>,
    highlightTextsData: string[],
  ): HTMLElement[][] => {
    const processedMark = new Set<Element>();
    const groups: HTMLElement[][] = [];
    const lowerHighlightTextsData = highlightTextsData.map((t) => t.toLowerCase());

    Array.from(markElements).forEach((mark, currentIndex) => {
      if (processedMark.has(mark)) return;

      let text = mark.textContent || '';
      let lowerText = text.toLowerCase();
      const group = [mark as HTMLElement];
      processedMark.add(mark);

      for (let nextIndex = currentIndex + 1; nextIndex < markElements.length; nextIndex += 1) {
        const nextMark = markElements[nextIndex];
        if (!processedMark.has(nextMark)) {
          const lastMarkInGroup = group[group.length - 1];
          if (shouldBreakMarkGrouping(lastMarkInGroup, nextMark)) {
            break;
          }

          const nextText = nextMark.textContent || '';
          const combinedText = text + nextText;
          const combinedLowerText = combinedText.toLowerCase();

          const startsWithCombinedText = (t: string) => t.startsWith(combinedLowerText);
          if (lowerHighlightTextsData.some(startsWithCombinedText)) {
            text = combinedText;
            lowerText = combinedLowerText;
            group.push(nextMark as HTMLElement);
            processedMark.add(nextMark);
            if (lowerHighlightTextsData.includes(lowerText)) break;
          } else {
            break;
          }
        }
      }

      const isFullMatch = lowerHighlightTextsData.includes(lowerText);
      const isSingleMatch = group.length === 1 && lowerHighlightTextsData.includes(group[0].textContent?.toLowerCase() || '');

      if (isFullMatch || isSingleMatch) {
        groups.push(group);
      }
    });

    return groups;
  };

  const processHighlights = (
    ocrResultList: OcrPage[],
    highlightTextsParams: string[],
  ): Promise<HighlightMap> => new Promise((resolve, reject) => {
    try {
      const tempContainer = buildGlobalTempContainer(ocrResultList);
      const marker = new Mark(tempContainer);

      const newHighlightMap: HighlightMap = [];

      marker.mark(highlightTextsParams, {
        acrossElements: true,
        filter: (node) => !node.parentElement?.closest('mark'),
        done: () => {
          const markedElements = tempContainer.querySelectorAll('mark');
          const markGroups = groupMarkElementsByHighlight(markedElements, highlightTextsParams);
          markGroups.forEach((group, i) => {
            const pageNo = group[0]?.closest('[data-page]')?.getAttribute('data-page');
            group.forEach((mark, index) => {
              const pageNoForGroup = group[index]?.closest('[data-page]')?.getAttribute('data-page');
              mark.setAttribute('data-highlight-index', String(i));
              mark.setAttribute('data-page', String(pageNoForGroup));
            });
            newHighlightMap.push({ pageNo: Number(pageNo) });
          });

          setGroupedMarks(markGroups);

          document.body.removeChild(tempContainer);
          resolve(newHighlightMap);
        },
      });
    } catch (error) {
      reject(error);
    }
  });

  const navigateToFirstHighlightPage = (newHighlightMapArray:HighlightMap) => {
    if (newHighlightMapArray.length > 0 && newHighlightMapArray[0].pageNo !== currentPage) {
      setCurrentPage(newHighlightMapArray[0].pageNo);
    }
  };

  const processAllHighlights = (
    ocrResultList: OcrPage[],
    highlightWordsParam: string[],
  ): void => {
    processHighlights(ocrResultList, highlightWordsParam)
      .then((newHighlightMap) => {
        newHighlightMap.sort((a, b) => a.pageNo - b.pageNo);
        setHighlightMap(newHighlightMap);
        setCurrentHighlightIndex(0);
        navigateToFirstHighlightPage(newHighlightMap);
      })
      .catch(() => {
        openMessageModal(ERROR_MESSAGES.UNEXPECTED_ERROR);
        setHighlightMap([]);
      });
  };

  const resetMarks = () => {
    markInstanceRef.current?.unmark();
  };

  const createMarkInstance = (container: HTMLElement) => {
    const marker = new Mark(container);
    markInstanceRef.current = marker;
    return marker;
  };

  const isGroupInsidePage = (group: HTMLElement[]) => group[0]?.closest('[data-page]') !== null;

  const highlightCurrentMark = (index: number) => {
    if (!ocrContainerRef.current) return;
    ocrContainerRef.current.querySelectorAll(`.${style.currentHighlight}`)
      .forEach((el) => el.classList.remove(style.currentHighlight));

    const marksOnPage = ocrContainerRef.current.querySelectorAll('.ocr-highlight-mark');
    const markGroups = groupMarkElementsByHighlight(marksOnPage, highlightWords);

    const currentHighlightGroups = markGroups.filter(isGroupInsidePage);
    const incompleteWordGroups = markGroups.filter((g) => !isGroupInsidePage(g));

    if (incompleteWordGroups.length > 0) {
      const firstMarkOnCurrentPage = (groupedMarks)?.flat()
        .find((el) => el.getAttribute('data-page') === String(currentPage)) ?? null;

      const firstMarkIndex = firstMarkOnCurrentPage?.getAttribute('data-highlight-index');
      if (firstMarkIndex === String(currentHighlightIndex)) {
        const brokenPageWordMark = markGroups[0][1] || markGroups[0][0];
        brokenPageWordMark.classList.add(style.currentHighlight);
      }
    }

    if (currentHighlightGroups[index]) {
      currentHighlightGroups[index].forEach((mark) => {
        mark.classList.add(style.currentHighlight);
      });

      currentHighlightGroups[index][0].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  const handleMarkDone = () => {
    if (totalMatches === 0) return;

    const highlightsOnPreviousPages = highlightMap.filter((h) => h.pageNo < currentPage).length;
    const localHighlightIndex = currentHighlightIndex - highlightsOnPreviousPages;

    highlightCurrentMark(localHighlightIndex);
  };

  // effects
  useEffect(() => {
    if (ocrContainerRef.current) {
      setCopiedText(ocrContainerRef.current.innerText);
    }
  }, [selectedOCRData]);

  useEffect(() => {
    const previousPageData = ocrDetailData?.ocrResultList.find((item) => item.pageNo === currentPage - 1);
    const nextPageData = ocrDetailData?.ocrResultList.find((item) => item.pageNo === currentPage + 1);
    const newData = ocrDetailData?.ocrResultList.find((item) => item.pageNo === currentPage) || null;
    setSelectedOCRData(newData);
    setPreviousPageLastLineText(previousPageData?.ocrDetailResultList[previousPageData.ocrDetailResultList.length - 1]?.ocrText || null);
    setNextPageFirstLineText(nextPageData?.ocrDetailResultList[0]?.ocrText || null);
  }, [currentPage, ocrDetailData?.ocrResultList]);

  useEffect(() => {
    localStorage.removeItem('pdfjs.history');
    return () => {
      localStorage.removeItem('pdfjs.history');
    };
  }, []);

  useEffect(() => {
    if (!ocrDetailData?.ocrResultList || highlightWords.length === 0) {
      setHighlightMap([]);
      return;
    }

    processAllHighlights(ocrDetailData.ocrResultList, highlightWords);
  }, [ocrDetailData]);

  useEffect(() => {
    if (!ocrContainerRef.current || highlightWords.length === 0) return;

    resetMarks();

    const marker = createMarkInstance(ocrContainerRef.current);

    marker.mark(highlightWords, {
      acrossElements: true,
      filter: (node) => !node.parentElement?.closest('mark'),
      each: (element) => element.classList.add('ocr-highlight-mark'),
      done: () => handleMarkDone(),
    });

    // eslint-disable-next-line consistent-return
    return () => {
      resetMarks();
    };
  }, [selectedOCRData, currentHighlightIndex, highlightMap]);

  return (
    <div className={style.modalOverlay} style={{ display: open ? 'block' : 'none' }}>
      <div className={style.alertBox}>
        <div className={style.modal}>
          <div className={style.modalContainer}>
            <div className={style.modalBody}>
              { !ocrDetailData ? (
                <div className={style.loadingContainer}>
                  <Spinner />
                </div>
              )
                : (
                  <>
                    <PdfPreviewSideSlide currentPage={currentPage} setCurrentPage={setCurrentPage} className={style.imagesContainerSection} pdfFile={pdfFile} />
                    <div className={style.pdfFileContainer}>
                      <PdfOCRPreview
                        ocrData={ocrDetailData?.ocrResultList}
                        pdfFile={pdfFile}
                        currentPage={currentPage}
                      />
                    </div>
                    <div className={style.ocrContainer}>
                      <div className={style.docTitleContainer}>
                        <BaseText size="smMd">{ocrDetailData?.documentName}</BaseText>
                      </div>

                      {isSearching && (
                        <div className={style.highlightWordWrapper}>
                          <div style={{ minWidth: 80 }}>
                            <BaseText size="sm">
                              キーワード：
                            </BaseText>
                          </div>
                          <div
                            className={style.searchKeyword}
                            title={searchKeyword || ''}
                          >
                            <BaseText size="sm">
                              {searchKeyword}
                            </BaseText>
                          </div>
                          <div
                            className={style.highlightCounter}
                          >
                            <div style={{ minWidth: 75, textAlign: 'right' }}>
                              <BaseText size="sm">
                                {totalMatches > 0 ? currentHighlightIndex + 1 : 0}
                                {' '}
                                /
                                {' '}
                                {totalMatches}
                              </BaseText>
                            </div>
                            <img
                              src="/images/chevron-up.svg"
                              className={`
                              ${style.arrowImg} 
                              ${style.clickable} 
                              ${style.arrowUp}
                              ${totalMatches > 1 ? style.arrowActive : style.arrowDisabled}
                              `}
                              alt="Anterior"
                              onClick={handlePreviousHighlight}
                            />
                            <img
                              src="/images/chevron-down.svg"
                              className={`
                              ${style.arrowImg} 
                              ${style.clickable} 
                              ${style.arrowDown}
                              ${totalMatches > 1 ? style.arrowActive : style.arrowDisabled}
                              `}
                              alt="Próximo"
                              onClick={handleNextHighlight}
                            />
                          </div>
                        </div>
                      )}

                      <div className={style.ocrContainerBody}>
                        <div
                          className={`${style.copyButtonContainer} ${
                            isSearching ? style.copyButtonTopLarge : style.copyButtonTopSmall
                          }`}
                        >
                          <CopyButton
                            textToCopy={copiedText}
                            itemId="callbackUrl"
                            setCopiedItemId={setCopiedItemId}
                            copiedItemId={copiedItemId}
                            showTooltip={false}
                            showCopyText
                            iconWidth={12}
                          />
                        </div>
                        <div ref={ocrContainerRef} className={style.ocrPreviewHighlightTarget}>
                          <span style={{ display: 'none' }}>{ previousPageLastLineText }</span>

                          {selectedOCRData?.ocrDetailResultList.map((item, index) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <div key={index} className={style.ocrItem} data-line-id={index + 1} data-page={currentPage}>

                              <span>{item.ocrText}</span>
                            </div>
                          ))}
                          <span style={{ display: 'none' }}>{ nextPageFirstLineText }</span>
                        </div>
                      </div>
                    </div>
                  </>
                )}
            </div>
            <div className={style.modalFooter}>
              <Button className={mainStyles['mr-10']} color="lightGray" size="smaller" onClick={handleCloseModal}>
                閉じる
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
